/* eslint-disable max-lines */
import {
    useContext, useEffect, useRef, useState
} from 'react';
import {
    KBContext
} from '../knowledgeBuilderContext/KBGlobalContext';
import {
    useParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    getCOSFormatDirectoryFromKey
} from './utils/fileUtils';
import {
    milvusService
} from 'shared/api/services/PROJECT-O/Milvus.service';
import {
    kbWSService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/KnowledgeBuilder.websocket.service';
import {
    kbService
} from 'shared/api/services/PROJECT-O/KnowledgeBuilder.service';
import {
    FileManagerContext
} from 'shared/context/FileManagerContext';

/**
 * Hook for managing knowledge base processing actions and states.
 * Handles L1 and L2 processing types, contextual retrieval modes, and filtering options.
 *
 */
export default function useKBAction() {
    // Chosen actions
    const [processType, setProcessType] = useState(null);
    const [levelType, setLevelType] = useState(null);
    const [contextualRetrievalMode, setContextualRetrievalMode] = useState(null);
    const [l1Filter, setL1Filter] = useState(null);
    const [l2Filter, setL2Filter] = useState(null);
    const [l2IsProcessing, l2SetIsProcessing] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFilter, setExportFilter] = useState(null);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [l2Instruction, setL2Instruction] = useState(null);
    const [l2BatchSize, setL2BatchSize] = useState(10);
    const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);
    const [currentlyProcessingFiles, setCurrentlyProcessingFiles] = useState([]);
    const [currentlyProcessingL2Files, setCurrentlyProcessingL2Files] = useState([]);
    const [l2ModelType, setL2ModelType] = useState(null);

    // Query side
    const [queryResults, setQueryResults] = useState([]);
    const [queryLoading, setQueryLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState(null);
    const [rerank, setRerank] = useState(false);
    const [topK, setTopK] = useState(10);
    const [topN, setTopN] = useState(3);

    const {
        tableHandler, updateFileStatus, refreshList
    } = useContext(KBContext);
    const {
        addNewNotifcation
    } = useContext(Context);

    const {
        oragID, subOragID, collectionId
    } = useParams();
    const {
        user
    } = useContext(Context);
    const {
        socket, lastL1Message, lastL2Message
    } = useContext(FileManagerContext);
    const QueryOffset = useRef(0);
    const OriginalQueryRequest = useRef(null);
    const QueryResultDetails = useRef(null);
    const resetQueryResults = () => {
        setQueryResults([]);
    };

    // Update status of currently processing files when global L1 status changes
    useEffect(() => {
        if (lastL1Message) {
            if (lastL1Message.status === 'processing') {
                // setL1ProcessingStarted(true);
            } else if (currentlyProcessingFiles.length > 0) {
                if (lastL1Message.status === 'completed' || lastL1Message.status === 'success') {
                    // updateFileStatus(currentlyProcessingFiles, 'success');
                    setCurrentlyProcessingFiles([]);
                    refreshList();
                } else if (lastL1Message.status === 'error' || lastL1Message.status === 'cancelled' || lastL1Message.status === 'failed') {
                    updateFileStatus(currentlyProcessingFiles, 'failed');
                    setCurrentlyProcessingFiles([]);
                    refreshList();
                }
            }
        }
    }, [lastL1Message, currentlyProcessingFiles, updateFileStatus, refreshList]);

    const outputFIleKeysFilter = () => {
        console.log('Process type ', processType);
        if (processType.value === 'l1' || processType.value === 'l2') {
            if (l1Filter?.value === 'selected' || l2Filter?.value === 'selected') {
                return (
                    Array.from(tableHandler.selectedDocs)?.map((item) =>
                        getCOSFormatDirectoryFromKey(item)
                    ) || []
                );
            } else {
                return [];
            }
        }
    };

    const outputFileKeysFilterExport = () => {
        console.log('Level type: ', levelType);
        if (levelType.value === 'l1' || levelType.value === 'l2') {
            if (exportFilter?.value === 'selected') {
                return (
                    Array.from(tableHandler.selectedDocs)?.map((item) =>
                        getCOSFormatDirectoryFromKey(item)
                    ) || []
                );
            } else {
                return [];
            }
        }
    };

    const executeL1Processing = async () => {
        console.log('Contextual Retrieval', contextualRetrievalMode);

        // Update status of selected files to "processing" before starting
        const selectedFileKeys = outputFIleKeysFilter();
        if (selectedFileKeys.length > 0) {
            setCurrentlyProcessingFiles(selectedFileKeys);
            updateFileStatus(selectedFileKeys, 'processing');
        }

        const body = {
            organization_id: oragID,
            sub_organization_id: subOragID,
            collection_id: collectionId,
            prefix: '', // All files
            status_filter: l1Filter.value == 'selected' ? '' : l1Filter.value,
            contextual_embedding_strategy: contextualRetrievalMode
                ? contextualRetrievalMode?.value
                : '',
            file_keys_filter: outputFIleKeysFilter(),
            user: user?.name || 'Unrecognized user',
        };

        console.log('L1 BODY', body);
        try {
            await kbWSService.get_l1_task_id_with_creds(body);
        } catch (error) {
            console.error('Error sending L1 processing request', error);
            // Reset status on error
            if (selectedFileKeys.length > 0) {
                updateFileStatus(selectedFileKeys, 'idle');
            }
            throw error;
        }
    };
    const executeL2Processing = async () => {
        if (!l2Filter || !l2Instruction) {
            addNewNotifcation(
                'L2 Filter and Instruction cannot be blank!',
                'warning'
            );
        } else {
            l2SetIsProcessing(true);
            const selectedFileKeys = outputFIleKeysFilter();
            if (selectedFileKeys.length > 0) {
                setCurrentlyProcessingL2Files(selectedFileKeys);
                updateFileStatus(selectedFileKeys, 'processing', 'l2');
            }

            const body = {
                organization_id: oragID,
                sub_organization_id: subOragID,
                collection_id: collectionId,
                instruction_name: l2Instruction.unique_name,
                status_filter: l2Filter.value == 'selected' ? '' : l2Filter.value,
                file_keys_filter: outputFIleKeysFilter(),
                batch_size: l2BatchSize || 10,
                model_type: l2Instruction.model_type || '',
                is_use_batch_predict: false,
            };

            console.log('L2 BODY', body);
            try {
                const res = await kbWSService.l2_processing(body);
                if (socket.readyState === WebSocket.OPEN) {
                    socket.send(
                        JSON.stringify({
                            task_id: res.data.task_id,
                        })
                    );
                }
            } catch (error) {
                console.error('Error sending L2 processing request', error);
            } finally {
                l2SetIsProcessing(false);
            }
        }
    };

    // Update status of currently processing L2 files when global L2 status changes
    useEffect(() => {
        if (lastL2Message) {
            if (lastL2Message.status === 'processing') {
                // setL2ProcessingStarted(true);
            } else if (currentlyProcessingL2Files.length > 0) {
                if (lastL2Message.status === 'completed' || lastL2Message.status === 'success') {
                    // updateFileStatus(currentlyProcessingL2Files, 'success', 'l2');
                    setCurrentlyProcessingL2Files([]);
                    refreshList();
                } else if (lastL2Message.status === 'error' || lastL2Message.status === 'cancelled' || lastL2Message.status === 'failed') {
                    updateFileStatus(currentlyProcessingL2Files, 'failed', 'l2');
                    setCurrentlyProcessingL2Files([]);
                    refreshList();
                }
            }
        }
    }, [lastL2Message, currentlyProcessingL2Files, updateFileStatus, refreshList]);

    const executeExport = async () => {
        if (!exportFilter || !levelType) {
            addNewNotifcation(
                'Export filter and level type cannot be blank!',
                'warning'
            );
        } else {
            setIsExporting(true);
            const body = {
                organization_id: oragID,
                sub_organization_id: subOragID,
                collection_id: collectionId,
                file_keys_filter: outputFileKeysFilterExport(),
                level: levelType.value,
            };

            console.log('EXPORT BODY: ', body);

            try {
                const response = await kbService.exportSearchResults(body);

                // Handle CSV file download
                const blob = new Blob([response.data], {
                    type: 'text/csv',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                // Extract filename from Content-Disposition header if available
                const contentDisposition = response.headers['content-disposition'];
                let filename = 'KB_export.csv';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(
                        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                    );
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                addNewNotifcation('Export completed successfully!', 'success');
            } catch (error) {
                console.error('Error exporting data', error);
                addNewNotifcation('Error exporting data. Please try again.', 'danger');
            } finally {
                setIsExporting(false);
            }
        }
    };

    const sendQuery = async (e) => {
    // TODO: have the logic to queryconst handleSendQuery = async (e) => {
        e.preventDefault();

        setQueryResults([]);
        setSelectedQuery(null);
        setQueryLoading(true);
        try {
            setQueryLoading(true);
            QueryOffset.current = 0;
            QueryResultDetails.current = null;
            OriginalQueryRequest.current = null;
            const currentOffset = QueryOffset.current;
            const fileNames = Array.from(tableHandler.selectedDocs).map((doc) =>
                getCOSFormatDirectoryFromKey(doc)
            );
            const body = {
                organization_id: oragID,
                sub_organization_id: subOragID,
                collection_id: collectionId,
                query_text: searchQuery,
                offset: currentOffset,
                top_k: topK,
                top_n: topN,
                filters: {},
                file_keys_filter: fileNames.length > 0 ? fileNames : [],
                use_rerank: rerank,
                search_type: searchType.value,
            };
            const res = await milvusService.hybridSearch(body);
            console.log('Search Response:', res);
            OriginalQueryRequest.current = body;
            const responseData = res.data.data || res.data;

            const remainingItems = Math.max(
                0,
                responseData.total_count - (responseData.offset + responseData.limit)
            );
            QueryOffset.current = Math.min(
                responseData.offset + responseData.limit,
                responseData.total_count
            );

            QueryResultDetails.current = {
                query: searchQuery,
                total: responseData.total_count,
                offset: responseData.offset,
                limit: responseData.limit,
                remaining_items: remainingItems,
            };
            setQueryResults(responseData.results || []);
            // const results = res.data.results || [];
            console.log('Query Result Details', QueryResultDetails.current);
        } catch (error) {
            console.error('Error sending query', error);
        } finally {
            setQueryLoading(false);
        }
    };

    const loadMoreQuery = async (e) => {
        e.preventDefault();
        setQueryLoading(true);

        try {
            const currentOffset = QueryOffset.current;
            const limit = QueryResultDetails.current.limit;
            const total = QueryResultDetails.current.total;

            const remainingItems = Math.max(0, total - currentOffset);

            if (remainingItems <= 0) {
                return;
            }

            const adjustedLimit = Math.min(limit, remainingItems);

            const body = {
                ...OriginalQueryRequest.current,
                offset: currentOffset,
                limit: adjustedLimit, // Use adjusted limit
            };

            const res = await milvusService.hybridSearch(body);
            console.log('Load More Response:', res);
            const responseData = res.data.data || res.data;

            const newRemainingItems = Math.max(
                0,
                responseData.total_count - (currentOffset + adjustedLimit)
            );

            QueryOffset.current = currentOffset + adjustedLimit;

            QueryResultDetails.current = {
                query: QueryResultDetails.current.query,
                total: responseData.total_count,
                offset: responseData.offset,
                limit: responseData.limit,
                remaining_items: newRemainingItems, // Update with new remaining items
            };
            console.log('Query Result Details', QueryResultDetails.current);

            setQueryResults((prevResults) => [
                ...prevResults,
                ...(responseData.results || []),
            ]);
        } catch (error) {
            console.error('Error loading more query', error);
        } finally {
            setQueryLoading(false);
        }
    };

    return {
    // ==== States ====
    /**
     * @type {import("../Components/KBDropdown").KBDropdownOption}
     * Selected process type
     */
        l2IsProcessing,
        isExporting,
        processType,
        levelType,
        l1Filter,
        l2Filter,
        exportFilter,
        contextualRetrievalMode,
        searchQuery,
        searchType,
        rerank,
        topK,
        topN,
        queryResults,
        queryLoading,
        selectedQuery,
        l2Instruction,
        setL2Instruction,
        l2BatchSize,
        setL2BatchSize,
        isLoadingInstructions,
        setIsLoadingInstructions,

        // ==== Setters ====
        /**
     * Sets the process type
     */
        setProcessType,
        setLevelType,
        // ==== Action Functions ====
        /**
     * Trigger the L1 processing
     */
        executeL1Processing,
        /**
     * Trigger the L2 processing
     */
        executeL2Processing,
        executeExport,
        /**
     * A function to query documents
     */
        sendQuery,
        loadMoreQuery,
        setContextualRetrievalMode,
        setL1Filter,
        setL2Filter,
        setExportFilter,
        setSearchQuery,
        setSearchType,
        setRerank,
        setTopK,
        setTopN,
        setQueryLoading,
        resetQueryResults,
        setSelectedQuery,
        l2ModelType,
        setL2ModelType,
        // ==== Refs ====
        QueryResultDetails,
    };
}
